/**
 * PortalProgramLaunch — 1, 2, 3 wizard for activating a program
 * for a real consumer.
 *
 * Step 1 · Program + start date        → chooses from the clinic's active activations
 * Step 2 · Consumer info               → first name, last name, DOB, phone, email
 * Step 3 · Confirm + launch            → summary + fires POST /clinic/program-launch
 *
 * On success:
 *  - Vendors linked to the activation receive a service-request email
 *  - A program_enrollments row is created (feeds existing analytics)
 *  - Success screen shows per-vendor dispatch status + option to launch another
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ToolPageShell } from "@/components/AdminTools";
import {
  AlertCircle, ArrowLeft, ArrowRight, CalendarDays, CheckCircle2, Loader2, Mail,
  Phone, Rocket, User, XCircle,
} from "lucide-react";

// ---------------------------------------------------------------
// Small validation helpers — kept inline to avoid a shared util
// import; the flow is short enough to justify local logic.
// ---------------------------------------------------------------
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_DIGIT_RE = /\d/g;

function todayISO() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}

function computeDueBy(startISO, durationDays) {
  if (!startISO) return "";
  const days = Math.max(Number(durationDays) || 30, 7);
  const d = new Date(startISO + "T00:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function StepBadge({ n, label, state }) {
  const stateClass =
    state === "done"
      ? "bg-emerald-100 text-emerald-700 border-emerald-200"
      : state === "current"
        ? "bg-slate-900 text-white border-slate-900"
        : "bg-white text-slate-400 border-slate-200";
  return (
    <div className="flex items-center gap-2" data-testid={`launch-step-${n}`}>
      <div
        className={`w-6 h-6 rounded-full border flex items-center justify-center text-[11px] font-bold ${stateClass}`}
      >
        {state === "done" ? <CheckCircle2 className="w-3.5 h-3.5" /> : n}
      </div>
      <div className={`text-[11px] uppercase tracking-[0.12em] font-semibold ${state === "current" ? "text-slate-900" : "text-slate-400"}`}>
        {label}
      </div>
    </div>
  );
}

function StepStrip({ step }) {
  return (
    <div className="flex items-center gap-4 mb-6">
      <StepBadge n={1} label="Program & date" state={step === 1 ? "current" : "done"} />
      <div className="flex-1 h-px bg-slate-200" />
      <StepBadge n={2} label="Consumer" state={step === 2 ? "current" : step > 2 ? "done" : "todo"} />
      <div className="flex-1 h-px bg-slate-200" />
      <StepBadge n={3} label="Launch" state={step === 3 ? "current" : step > 3 ? "done" : "todo"} />
    </div>
  );
}

// ---------------------------------------------------------------
// Empty state — clinic has no activated programs.
// ---------------------------------------------------------------
function EmptyState() {
  return (
    <div
      className="rounded-2xl border border-slate-200 bg-white p-8 text-center max-w-lg mx-auto"
      data-testid="launch-empty-state"
    >
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-50 text-blue-600 mb-3">
        <Rocket className="w-6 h-6" />
      </div>
      <h3 className="font-display text-lg font-bold text-slate-900">No programs activated yet</h3>
      <p className="mt-2 text-sm text-slate-600 leading-relaxed">
        You need to activate a program before you can launch it for a consumer. Head to the Services Catalog to pick one and select your vendors.
      </p>
      <Link
        to="/portal/catalog"
        data-testid="launch-empty-cta"
        className="mt-5 inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-full px-4 py-2 text-sm font-semibold"
      >
        Browse catalog <ArrowRight className="w-3.5 h-3.5" />
      </Link>
    </div>
  );
}

// ---------------------------------------------------------------
// Success state — after POST /clinic/program-launch returns.
// ---------------------------------------------------------------
function SuccessState({ launch, onLaunchAnother }) {
  const { dispatch_summary: summary = {}, dispatches = [], consumer = {} } = launch;
  const anyFailed = (summary.failed || 0) > 0;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="launch-success">
      <div className={`${anyFailed ? "bg-amber-50" : "bg-emerald-50"} border-b border-slate-200 p-5`}>
        <div className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] font-bold ${anyFailed ? "text-amber-700" : "text-emerald-700"}`}>
          {anyFailed ? <AlertCircle className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}
          Program launched
        </div>
        <h2 className="mt-2 font-display text-2xl font-bold text-slate-900">
          {launch.program_name}
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          For {consumer.first_name} {consumer.last_name} · Start {launch.start_date} · Due {launch.complete_by}
        </p>
        <div className="mt-3 text-xs text-slate-600">
          {summary.sent}/{summary.total} vendor notification{summary.total === 1 ? "" : "s"} sent
          {anyFailed && ` · ${summary.failed} failed`}
        </div>
      </div>

      <div className="p-5">
        <h3 className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-3">Vendor dispatches</h3>
        <div className="space-y-2">
          {dispatches.map((d) => (
            <div
              key={d.id}
              data-testid={`launch-dispatch-${d.vendor_id}`}
              className={`flex items-start gap-3 p-3 rounded-lg border ${
                d.dispatched ? "border-emerald-200 bg-emerald-50/40" : "border-rose-200 bg-rose-50/40"
              }`}
            >
              {d.dispatched ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
              ) : (
                <XCircle className="w-4 h-4 text-rose-600 flex-shrink-0 mt-0.5" />
              )}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-900">{d.vendor_name}</div>
                <div className="text-[11px] text-slate-500">
                  {d.category}
                  {d.contact_email && ` · ${d.contact_email}`}
                </div>
                {d.error && (
                  <div className="text-[11px] font-mono text-rose-700 mt-1">{d.error}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="p-5 border-t border-slate-100 flex items-center justify-between flex-wrap gap-3">
        <div className="text-[11px] text-slate-500">
          A patient enrollment record was created. Vendors will engage the consumer directly.
        </div>
        <button
          type="button"
          onClick={onLaunchAnother}
          data-testid="launch-another"
          className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-full px-4 py-2 text-sm font-semibold"
        >
          <Rocket className="w-3.5 h-3.5" /> Launch another
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------
// Main page.
// ---------------------------------------------------------------
export default function PortalProgramLaunch() {
  const [loading, setLoading] = useState(true);
  const [launchable, setLaunchable] = useState([]);
  const [step, setStep] = useState(1);
  const [activationId, setActivationId] = useState("");
  const [startDate, setStartDate] = useState(todayISO());
  const [consumer, setConsumer] = useState({
    first_name: "", last_name: "", dob: "", phone: "", email: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [launchResult, setLaunchResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/clinic/program-launch/programs");
      setLaunchable(r.data?.launchable || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const selectedActivation = useMemo(
    () => launchable.find((x) => x.activation_id === activationId) || null,
    [launchable, activationId],
  );

  const dueBy = useMemo(
    () => computeDueBy(startDate, selectedActivation?.avg_program_duration_days),
    [startDate, selectedActivation],
  );

  const step1Valid = !!activationId && !!startDate && startDate >= todayISO();
  const phoneDigits = (consumer.phone.match(PHONE_DIGIT_RE) || []).length;
  const step2Valid =
    consumer.first_name.trim().length > 0 &&
    consumer.last_name.trim().length > 0 &&
    consumer.dob &&
    phoneDigits >= 7 &&
    EMAIL_RE.test(consumer.email.trim());

  const resetAll = () => {
    setStep(1);
    setActivationId("");
    setStartDate(todayISO());
    setConsumer({ first_name: "", last_name: "", dob: "", phone: "", email: "" });
    setLaunchResult(null);
    load();
  };

  const submit = async () => {
    if (!step1Valid || !step2Valid) return;
    setSubmitting(true);
    try {
      const r = await api.post("/clinic/program-launch", {
        activation_id: activationId,
        start_date: startDate,
        consumer: {
          first_name: consumer.first_name.trim(),
          last_name: consumer.last_name.trim(),
          dob: consumer.dob,
          phone: consumer.phone.trim(),
          email: consumer.email.trim(),
        },
      });
      setLaunchResult(r.data);
      toast.success(`Program launched · ${r.data?.dispatch_summary?.sent}/${r.data?.dispatch_summary?.total} vendor(s) notified`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ToolPageShell
      eyebrow="Clinic · Consumer Programs"
      title="Launch program"
      intro="Activate a program for a real consumer in three steps. Selected vendors are notified automatically with the consumer's contact info + service due-by date."
    >
      {loading && (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 flex items-center justify-center text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      )}

      {!loading && launchable.length === 0 && !launchResult && <EmptyState />}

      {!loading && launchResult && (
        <SuccessState launch={launchResult} onLaunchAnother={resetAll} />
      )}

      {!loading && launchable.length > 0 && !launchResult && (
        <div className="max-w-2xl mx-auto">
          <StepStrip step={step} />

          {/* ---------------- Step 1 ---------------- */}
          {step === 1 && (
            <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-5" data-testid="launch-step-1-panel">
              <div>
                <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Program</label>
                <select
                  value={activationId}
                  onChange={(e) => setActivationId(e.target.value)}
                  data-testid="launch-program-select"
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white"
                >
                  <option value="">Choose a program…</option>
                  {launchable.map((x) => (
                    <option key={x.activation_id} value={x.activation_id}>
                      {x.program_name} · {x.vendor_count} vendor{x.vendor_count === 1 ? "" : "s"}
                    </option>
                  ))}
                </select>
                {selectedActivation && (
                  <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs">
                    <div className="text-slate-500 uppercase tracking-wider font-bold text-[10px] mb-1">Vendors that will be notified</div>
                    <div className="flex flex-wrap gap-1.5">
                      {(selectedActivation.vendor_selections || []).map((v) => (
                        <span
                          key={v.vendor_id}
                          data-testid={`launch-selected-vendor-${v.vendor_id}`}
                          className="inline-flex items-center gap-1 rounded-full bg-white border border-slate-200 px-2 py-0.5 text-[11px] text-slate-700 font-medium"
                        >
                          {v.vendor_name} <span className="text-slate-400">· {v.category}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500 inline-flex items-center gap-1.5">
                  <CalendarDays className="w-3 h-3" /> Start date
                </label>
                <input
                  type="date"
                  value={startDate}
                  min={todayISO()}
                  onChange={(e) => setStartDate(e.target.value)}
                  data-testid="launch-start-date"
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm"
                />
                {dueBy && selectedActivation && (
                  <div className="mt-2 text-[11px] text-slate-500">
                    Vendors will be asked to complete service by <strong className="text-slate-900">{dueBy}</strong>
                    {selectedActivation.avg_program_duration_days
                      ? ` (${selectedActivation.avg_program_duration_days}-day program)`
                      : " (30 days default)"}
                  </div>
                )}
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  disabled={!step1Valid}
                  onClick={() => setStep(2)}
                  data-testid="launch-next-1"
                  className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 disabled:opacity-40 text-white rounded-full px-5 py-2.5 text-sm font-semibold"
                >
                  Continue <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}

          {/* ---------------- Step 2 ---------------- */}
          {step === 2 && (
            <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4" data-testid="launch-step-2-panel">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="First name" icon={<User className="w-3 h-3" />}>
                  <input
                    type="text"
                    value={consumer.first_name}
                    onChange={(e) => setConsumer((c) => ({ ...c, first_name: e.target.value }))}
                    data-testid="launch-consumer-first-name"
                    className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm"
                  />
                </Field>
                <Field label="Last name">
                  <input
                    type="text"
                    value={consumer.last_name}
                    onChange={(e) => setConsumer((c) => ({ ...c, last_name: e.target.value }))}
                    data-testid="launch-consumer-last-name"
                    className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm"
                  />
                </Field>
              </div>
              <Field label="Date of birth" icon={<CalendarDays className="w-3 h-3" />}>
                <input
                  type="date"
                  value={consumer.dob}
                  max={todayISO()}
                  onChange={(e) => setConsumer((c) => ({ ...c, dob: e.target.value }))}
                  data-testid="launch-consumer-dob"
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm"
                />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Phone" icon={<Phone className="w-3 h-3" />}>
                  <input
                    type="tel"
                    value={consumer.phone}
                    onChange={(e) => setConsumer((c) => ({ ...c, phone: e.target.value }))}
                    placeholder="(555) 123-4567"
                    data-testid="launch-consumer-phone"
                    className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm"
                  />
                </Field>
                <Field label="Email" icon={<Mail className="w-3 h-3" />}>
                  <input
                    type="email"
                    value={consumer.email}
                    onChange={(e) => setConsumer((c) => ({ ...c, email: e.target.value }))}
                    placeholder="jane@example.com"
                    data-testid="launch-consumer-email"
                    className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm"
                  />
                </Field>
              </div>

              <div className="flex justify-between pt-2">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  data-testid="launch-back-2"
                  className="inline-flex items-center gap-1.5 border border-slate-300 text-slate-700 hover:bg-slate-100 rounded-full px-4 py-2 text-sm font-semibold"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> Back
                </button>
                <button
                  type="button"
                  disabled={!step2Valid}
                  onClick={() => setStep(3)}
                  data-testid="launch-next-2"
                  className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 disabled:opacity-40 text-white rounded-full px-5 py-2.5 text-sm font-semibold"
                >
                  Continue <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}

          {/* ---------------- Step 3 ---------------- */}
          {step === 3 && selectedActivation && (
            <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="launch-step-3-panel">
              <div className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-500 mb-2">Confirm & launch</div>
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 space-y-3 text-sm">
                <SummaryRow label="Program">{selectedActivation.program_name}</SummaryRow>
                <SummaryRow label="Start date">{startDate}</SummaryRow>
                <SummaryRow label="Complete service by">{dueBy}</SummaryRow>
                <SummaryRow label="Consumer">
                  {consumer.first_name} {consumer.last_name} · DOB {consumer.dob}
                </SummaryRow>
                <SummaryRow label="Contact">
                  <span className="font-mono text-xs">{consumer.email}</span> · <span className="font-mono text-xs">{consumer.phone}</span>
                </SummaryRow>
                <SummaryRow label={`Notifying (${selectedActivation.vendor_count})`}>
                  {(selectedActivation.vendor_selections || []).map((v) => v.vendor_name).join(", ")}
                </SummaryRow>
              </div>

              <div className="flex justify-between pt-5">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  disabled={submitting}
                  data-testid="launch-back-3"
                  className="inline-flex items-center gap-1.5 border border-slate-300 text-slate-700 hover:bg-slate-100 rounded-full px-4 py-2 text-sm font-semibold"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> Back
                </button>
                <button
                  type="button"
                  onClick={submit}
                  disabled={submitting}
                  data-testid="launch-submit"
                  className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-full px-6 py-2.5 text-sm font-semibold"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Launching…
                    </>
                  ) : (
                    <>
                      <Rocket className="w-3.5 h-3.5" /> Launch program
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </ToolPageShell>
  );
}

function Field({ label, icon, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500 inline-flex items-center gap-1.5">
        {icon} {label}
      </label>
      {children}
    </div>
  );
}

function SummaryRow({ label, children }) {
  return (
    <div className="flex items-start gap-3 text-sm">
      <div className="w-40 text-[11px] uppercase tracking-wider font-bold text-slate-500 pt-0.5">{label}</div>
      <div className="text-slate-900 flex-1">{children}</div>
    </div>
  );
}
